import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import {
  Activity, Download, RefreshCw, CheckCircle2, XCircle, AlertTriangle, Loader2,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const fmtDate = (s) => (s ? new Date(s).toLocaleString("ro-RO") : "—");

const STATUS_OPTIONS = [
  { v: "all", l: "Toate" },
  { v: "received", l: "Received" },
  { v: "processing", l: "Processing" },
  { v: "completed", l: "Completed" },
  { v: "failed", l: "Failed" },
  { v: "skipped", l: "Skipped" },
  { v: "retrying", l: "Retrying" },
];

const StatusBadge = ({ s }) => {
  if (s === "completed") return <Badge className="bg-emerald-100 text-emerald-800 text-xs">{s}</Badge>;
  if (s === "failed") return <Badge className="bg-rose-100 text-rose-800 text-xs">{s}</Badge>;
  if (s === "skipped") return <Badge className="bg-slate-100 text-slate-700 text-xs">{s}</Badge>;
  if (s === "retrying") return <Badge className="bg-amber-100 text-amber-800 text-xs">{s}</Badge>;
  return <Badge variant="outline" className="text-xs">{s}</Badge>;
};

export const SFHealthKPIs = ({ refreshKey = 0 }) => {
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchHealth = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/sf-integration/health-summary`);
      setHealth(r.data);
    } catch (e) {
      // silently ignore in KPI
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchHealth();
    const t = setInterval(fetchHealth, 30000);
    return () => clearInterval(t);
  }, [fetchHealth, refreshKey]);

  if (loading || !health) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-testid="sf-health-loading">
        {[1, 2, 3, 4].map((i) => (
          <Card key={i}>
            <CardContent className="pt-6">
              <div className="h-8 w-24 bg-slate-100 rounded animate-pulse" />
              <div className="h-4 w-32 bg-slate-100 rounded animate-pulse mt-2" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  const events = health.events_24h || {};
  const failedAlert = events.failed > 5;
  const dlqAlert = health.dlq_pending > 0;

  return (
    <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-testid="sf-health-kpis">
      <Card data-testid="sf-health-webhook">
        <CardHeader className="pb-2">
          <CardDescription>Status Webhook</CardDescription>
        </CardHeader>
        <CardContent>
          {health.webhook_enabled ? (
            <Badge className="bg-emerald-100 text-emerald-800">
              <CheckCircle2 className="w-3 h-3 mr-1" /> Activ
            </Badge>
          ) : (
            <Badge variant="secondary" className="bg-slate-100 text-slate-700">
              <XCircle className="w-3 h-3 mr-1" /> Dezactivat
            </Badge>
          )}
        </CardContent>
      </Card>
      <Card data-testid="sf-health-events">
        <CardHeader className="pb-2">
          <CardDescription>Evenimente 24h</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-3xl font-bold text-indigo-700">{events.total}</div>
          <div className="text-xs text-slate-500 mt-1">
            ✓ {events.completed || 0} · ⊘ {events.skipped || 0}
            {events.failed > 0 && (
              <span className="text-rose-700 ml-2">✗ {events.failed}</span>
            )}
          </div>
        </CardContent>
      </Card>
      <Card data-testid="sf-health-dlq" className={dlqAlert ? "border-rose-300 bg-rose-50/30" : ""}>
        <CardHeader className="pb-2">
          <CardDescription>DLQ Pending</CardDescription>
        </CardHeader>
        <CardContent>
          <div className={`text-3xl font-bold ${dlqAlert ? "text-rose-700" : "text-slate-400"}`}>
            {health.dlq_pending}
          </div>
          {dlqAlert && (
            <div className="text-xs text-rose-600 mt-1 flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" /> Atenție necesară
            </div>
          )}
        </CardContent>
      </Card>
      <Card data-testid="sf-health-last-event" className={failedAlert ? "border-amber-300" : ""}>
        <CardHeader className="pb-2">
          <CardDescription>Ultim eveniment</CardDescription>
        </CardHeader>
        <CardContent>
          {health.last_event ? (
            <>
              <div className="text-xs font-mono truncate">{health.last_event.event_id}</div>
              <div className="flex items-center gap-2 mt-1">
                <StatusBadge s={health.last_event.status} />
                <span className="text-xs text-slate-500">{fmtDate(health.last_event.received_at)}</span>
              </div>
            </>
          ) : (
            <div className="text-sm text-slate-400">Niciun eveniment</div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};


export const SFHistoryTab = () => {
  const [events, setEvents] = useState({ data: [], total: 0, trend: [] });
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ date_from: "", date_to: "", status: "all" });
  const [page, setPage] = useState(1);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: 50 };
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;
      if (filters.status && filters.status !== "all") params.status = filters.status;
      const r = await axios.get(`${API}/admin/sf-integration/events`, { params });
      setEvents(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, [filters, page]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const exportCsv = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.date_from) params.set("date_from", filters.date_from);
      if (filters.date_to) params.set("date_to", filters.date_to);
      if (filters.status && filters.status !== "all") params.set("status", filters.status);
      const r = await axios.get(
        `${API}/admin/sf-integration/events/export?${params.toString()}`,
        { responseType: "blob" }
      );
      const blob = new Blob([r.data], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "sf_events.csv";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Export descărcat");
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    }
  };

  // Build trend data
  const trendData = (events.trend || []).map((p) => ({
    day: p.day ? new Date(p.day).toLocaleDateString("ro-RO", { month: "short", day: "numeric" }) : "?",
    count: p.count,
  }));

  return (
    <div className="space-y-4" data-testid="sf-history-tab">
      <Card data-testid="sf-trend-chart">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Activity className="w-5 h-5 text-indigo-600" /> Evenimente per zi (ultimele 30 zile)
          </CardTitle>
        </CardHeader>
        <CardContent>
          {trendData.length === 0 ? (
            <div className="text-sm text-slate-400 text-center py-12" data-testid="sf-trend-empty">
              Niciun eveniment în această perioadă.
            </div>
          ) : (
            <div className="h-64 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={trendData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="day" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                  <Tooltip />
                  <Line type="monotone" dataKey="count" stroke="#4f46e5" strokeWidth={2}
                        dot={{ r: 3 }} activeDot={{ r: 5 }} name="Evenimente" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg">Detaliu evenimente</CardTitle>
              <CardDescription>Total: {events.total} (paginare 50/page)</CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={exportCsv} data-testid="sf-export-csv">
              <Download className="w-4 h-4 mr-1" /> Export CSV
            </Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mt-3">
            <div>
              <Label className="text-xs">De la</Label>
              <Input
                type="date"
                value={filters.date_from}
                onChange={(e) => setFilters((f) => ({ ...f, date_from: e.target.value }))}
                data-testid="sf-date-from"
              />
            </div>
            <div>
              <Label className="text-xs">Până la</Label>
              <Input
                type="date"
                value={filters.date_to}
                onChange={(e) => setFilters((f) => ({ ...f, date_to: e.target.value }))}
                data-testid="sf-date-to"
              />
            </div>
            <div>
              <Label className="text-xs">Status</Label>
              <Select value={filters.status} onValueChange={(v) => setFilters((f) => ({ ...f, status: v }))}>
                <SelectTrigger data-testid="sf-status-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map((o) => (
                    <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <Button onClick={() => { setPage(1); fetchData(); }} className="w-full" data-testid="sf-apply-filters">
                Aplică
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Table data-testid="sf-events-table">
            <TableHeader>
              <TableRow>
                <TableHead>Event ID</TableHead>
                <TableHead>Tip</TableHead>
                <TableHead>Caz / Dosar</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Primit la</TableHead>
                <TableHead>IP sursă</TableHead>
                <TableHead>Tentative</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={i}>
                    {[1, 2, 3, 4, 5, 6, 7].map((j) => (
                      <TableCell key={j}>
                        <div className="h-4 bg-slate-100 rounded animate-pulse" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : events.data.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-slate-500 py-8" data-testid="sf-events-empty">
                    Niciun eveniment.
                  </TableCell>
                </TableRow>
              ) : (
                events.data.map((e) => (
                  <TableRow key={e.id} data-testid={`sf-event-row-${e.id}`}>
                    <TableCell className="font-mono text-xs">{e.event_id}</TableCell>
                    <TableCell className="text-xs">{e.event_type}</TableCell>
                    <TableCell className="text-xs">
                      {e.case_number || "—"}
                      {e.dossier_number && <div className="text-slate-400">{e.dossier_number}</div>}
                    </TableCell>
                    <TableCell><StatusBadge s={e.status} /></TableCell>
                    <TableCell className="text-xs">{fmtDate(e.received_at)}</TableCell>
                    <TableCell className="text-xs font-mono">{e.source_ip || "—"}</TableCell>
                    <TableCell className="text-xs">{e.attempts}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          {events.total > 50 && (
            <div className="flex items-center justify-between mt-3">
              <span className="text-xs text-slate-500">
                Pagina {page} / {Math.ceil(events.total / 50)}
              </span>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" disabled={page === 1}
                         onClick={() => setPage((p) => p - 1)} data-testid="sf-prev">Anterior</Button>
                <Button size="sm" variant="outline" disabled={page * 50 >= events.total}
                         onClick={() => setPage((p) => p + 1)} data-testid="sf-next">Următor</Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SFHistoryTab;
